/**
 * English dictionary for the internal dashboard (post-login app), keyed by
 * `t()` calls scattered across components — see LanguageContext.js for how
 * lookups work. Spanish is never stored here: it lives inline as each call
 * site's fallback, so this file only grows as new `t()` calls are added.
 *
 * Keys are namespaced by component/feature (e.g. "sidebar.leads") purely to
 * keep this file navigable as it grows across the whole dashboard — the
 * namespace has no runtime meaning.
 */
export const EN_DICTIONARY = {
  // ── Sidebar.js — nav items ──────────────────────────────────────────
  'sidebar.editProfile': 'Edit Profile',
  'sidebar.home': 'Home',
  'sidebar.appearance': 'Appearance',
  'sidebar.leads': 'Leads',
  'sidebar.analytics': 'Analytics',
  'sidebar.campaigns': 'Events',
  'sidebar.new': 'New',
  'sidebar.integrations': 'Integrations',
  'sidebar.hub': 'Hub',
  'sidebar.followups': 'Follow-ups',
  'sidebar.developer': 'Developer',
  'sidebar.teams': 'Teams',
  'sidebar.teamAnalytics': 'Team Analytics',
  'sidebar.admin': 'Administration',
  'sidebar.productManagement': 'Product Management',
  'sidebar.orders': 'Orders',
  'sidebar.subscriptions': 'Subscriptions',
  'sidebar.groupProfile': 'Profile',
  'sidebar.groupGrowth': 'Growth',
  'sidebar.groupTeam': 'Team',
  'sidebar.groupAdmin': 'Administration',
  'sidebar.search': 'Search (⌘K)',
  'sidebar.shortcuts': 'Keyboard shortcuts (?)',
  'sidebar.expandMenu': 'Expand menu',
  'sidebar.collapseMenu': 'Collapse menu',
  'sidebar.teamAccess': 'Teams Access',
  'sidebar.freePlan': 'Free Plan',
  'sidebar.planPrefix': 'Plan',
  'sidebar.comparePlans': 'Compare plans',
  'sidebar.settings': 'Settings',
  'sidebar.viewCurrentProfile': 'View current profile',
  'sidebar.logout': 'Log out',
  'sidebar.requiresPlan': 'Requires {plan} plan',

  // ── LeadsPanel.js ────────────────────────────────────────────────────
  'leads.title': 'Leads',
  'leads.subtitle': 'Manage the contacts identified from your profile',
  'leads.estado.nuevo': 'New',
  'leads.estado.contactado': 'Contacted',
  'leads.estado.noContactado': 'Not contacted',
  'leads.urgent': 'Urgent',
  'leads.timeAgo.minutes': '{n}m ago',
  'leads.timeAgo.hours': '{n}h ago',
  'leads.timeAgo.days': '{n}d ago',
  'leads.onboarding.title': 'Welcome to Leads',
  'leads.onboarding.desc': 'Every time someone fills out your profile form or saves your card, it shows up here automatically. Review their info, change their status, and follow up without losing track of anyone.',
  'leads.onboarding.close': 'Got it',
  'leads.lockedBanner.singular': 'locked lead',
  'leads.lockedBanner.plural': 'locked leads',
  'leads.lockedBanner.suffix': 'this month — you went over the free plan’s quota of 5.',
  'leads.lockedBanner.upgrade': 'Upgrade to Pro',
  'leads.metrics.today': 'Leads today',
  'leads.metrics.responseRate': 'Response rate',
  'leads.metrics.overdue': 'Pending 24h+',
  'leads.filters.searchPlaceholder': 'Search by name, phone, or email...',
  'leads.filters.allStatuses': 'All statuses',
  'leads.filters.allTags': 'All tags',
  'leads.filters.allCampaigns': 'All campaigns',
  'leads.empty.title': 'No leads yet',
  'leads.empty.desc': 'They’ll show up here once someone fills out the contact form on your profile',
  'leads.empty.copyLink': 'Copy my profile link',
  'leads.toast.linkCopied': 'Profile link copied',
  'leads.toast.linkCopyError': 'Couldn’t copy the link',
  'leads.table.contact': 'Contact',
  'leads.table.info': 'Info',
  'leads.table.status': 'Status',
  'leads.table.urgency': 'Urgency',
  'leads.table.tags': 'Tags',
  'leads.table.identified': 'Identified',
  'leads.table.editTitle': 'Edit lead',
  'leads.table.contactTitle': 'Contact',
  'leads.table.noContactInfo': 'No email or phone on file',
  'leads.table.addToHubTitle': 'Add to Hub',
  'leads.modal.title': 'Lead Details',
  'leads.modal.name': 'Name',
  'leads.modal.status': 'Status',
  'leads.modal.phone': 'Phone',
  'leads.modal.email': 'Email',
  'leads.modal.description': 'Description',
  'leads.modal.tags': 'Tags',
  'leads.modal.newTagPlaceholder': 'New tag...',
  'leads.modal.addTag': 'Add',
  'leads.modal.cancel': 'Cancel',
  'leads.modal.save': 'Save Changes',
  'leads.toast.updateError': 'Error updating the lead',
  'leads.toast.moveError': 'Couldn’t move the lead. Please try again.',
  'leads.toast.addedToHub': 'Contact added to Hub successfully',
  'leads.toast.alreadyInHub': 'This contact is already in your Hub',
  'leads.toast.addToHubError': 'Error adding to Hub',

  // ── AnalyticsPanel.js ────────────────────────────────────────────────
  'analytics.platform.website': 'Website',
  'analytics.noData': 'Not enough data yet',
  'analytics.pie.emptyDesc': 'It will show up here once you have visits or saved contacts',
  'analytics.pie.saved': 'Saved',
  'analytics.pie.title': 'Interaction Breakdown',
  'analytics.exportFilename': 'Executive_Summary',
  'analytics.range.all': 'All',
  'analytics.selectProfile': 'Select a profile to view its analytics',
  'analytics.onboarding.title': 'Welcome to Analytics',
  'analytics.onboarding.desc': 'Here you can see how many people visit your profile, which social networks drive the most clicks, and how your activity evolves over time. The numbers update on their own as visits come in.',
  'analytics.metric.total_visits.label': 'Total Visits',
  'analytics.metric.total_visits.sub': 'Total reach',
  'analytics.metric.social_clicks_total.label': 'Social Clicks',
  'analytics.metric.social_clicks_total.sub': 'Social interactions',
  'analytics.metric.vcf_downloads.label': 'Contacts Saved',
  'analytics.metric.vcf_downloads.sub': 'VCF downloads',
  'analytics.metric.engagement_rate.label': 'Engagement Rate',
  'analytics.metric.engagement_rate.sub': 'Average engagement',
  'analytics.export': 'Export',
  'analytics.trend.title': 'Trend Over Time',
  'analytics.trend.allTime': 'All time',
  'analytics.social.title': 'Performance by Social Network',
  'analytics.social.totalClicks': 'total clicks',
  'analytics.social.emptyTitle': 'No clicks yet',
  'analytics.social.emptyDesc': 'They’ll show up here once someone clicks one of your social links',
  'analytics.mostEffective.title': 'Most Effective Network',
  'analytics.mostEffective.suffix': 'of social interactions',
  'analytics.mostEffective.emptyDesc': 'Try a wider date range or wait for more visits to come in',

  // ── TeamsPanel.js ────────────────────────────────────────────────────
  'teams.toast.genKeyError': 'Error generating access key',
  'teams.confirm.regenerate': 'Are you sure? The previous key will stop working immediately.',
  'teams.confirm.regenerateLabel': 'Regenerate',
  'teams.toast.regenKeyError': 'Error regenerating key',
  'teams.confirm.deactivate': 'Deactivate {name}’s access? They won’t be able to log in until you reactivate their key.',
  'teams.thisMember': 'this member',
  'teams.confirm.deactivateLabel': 'Deactivate access',
  'teams.toast.toggleError': 'Error changing status',
  'teams.companyInfo.error': 'Fill in the company name and industry to continue.',
  'teams.toast.saveCompanyError': 'Error saving company info',
  'teams.onboarding.subtitle': 'Before managing your team, tell us about your company',
  'teams.companyInfo.title': 'Your company’s info',
  'teams.companyInfo.desc': 'This helps us better understand the companies that use Contact.',
  'teams.companyInfo.name': 'Company name',
  'teams.companyInfo.namePlaceholder': 'E.g.: Acme Corporation',
  'teams.companyInfo.industry': 'Industry',
  'teams.companyInfo.industryPlaceholder': 'Select an industry…',
  'teams.companyInfo.description': 'Description',
  'teams.optional': 'optional',
  'teams.companyInfo.descPlaceholder': 'Brief description of your company',
  'teams.companyInfo.saving': 'Saving…',
  'teams.companyInfo.saveAndContinue': 'Save and continue',
  'teams.subtitle': 'Manage your team and corporate access',
  'teams.tabs.members': 'Members',
  'teams.tabs.brand': 'Brand',
  'teams.stats.totalMembers': 'Total members',
  'teams.stats.activeKeys': 'Active keys',
  'teams.stats.noKey': 'No key assigned',
  'teams.stats.activeRate': 'Team with active access',
  'teams.members.title': 'Team Members',
  'teams.members.searchPlaceholder': 'Search…',
  'teams.members.noResults': 'No results',
  'teams.members.noneYet': 'No members yet',
  'teams.members.noMatches': 'No matches for "{query}"',
  'teams.members.emptyDesc': 'Add profiles to your team to see them here',
  'teams.members.noRole': 'No role',
  'teams.members.active': 'Active',
  'teams.members.inactive': 'Inactive',
  'teams.members.hideDetails': 'Hide details',
  'teams.members.viewDetails': 'View details',
  'teams.activity.title': 'Recent activity',
  'teams.activity.noChanges': 'No changes in the last {days} days',
  'teams.accessKey.title': 'Access key',
  'teams.accessKey.regenerate': 'Regenerate',
  'teams.accessKey.deactivate': 'Deactivate',
  'teams.accessKey.activate': 'Activate',
  'teams.accessKey.generate': 'Generate key',
  'teams.keyModal.title': 'Access Key',
  'teams.keyModal.warning': 'Save this key somewhere safe. You won’t be able to see it again once you close this window.',
  'teams.keyModal.copied': 'Copied',
  'teams.keyModal.copy': 'Copy key',
  'teams.keyModal.desc': 'This key grants exclusive access to the profile through "Contact for Teams"',
  'teams.keyModal.close': 'Close',

  // ── constants/themes.js — shared by AppearanceEditor.js and BrandKitEditor.js
  'theme.blue': 'Professional Blue',
  'theme.green': 'Nature Green',
  'theme.purple': 'Modern Purple',
  'theme.dark': 'Elegant Dark',
  'theme.warm': 'Warm Corporate',
  'theme.ocean': 'Fresh Ocean',

  // ── AppearanceEditor.js ──────────────────────────────────────────────
  'appearance.toast.saved': 'Changes saved successfully',
  'appearance.selectProfile': 'Select a profile to customize',
  'appearance.title': 'Customize Appearance',
  'appearance.editing': 'Customizing',
  'appearance.unsaved': 'You have unsaved changes',
  'appearance.allSaved': 'Everything saved',
  'appearance.discard': 'Discard',
  'appearance.saving': 'Saving...',
  'appearance.saveChanges': 'Save changes',
  'appearance.brand.usesCompanyBrand': 'This profile uses your company’s brand.',
  'appearance.brand.usesCompanyBrandSub': 'The template and color below come from there.',
  'appearance.brand.applying': 'Applying...',
  'appearance.brand.customizeThisProfile': 'Customize only this profile',
  'appearance.brand.doesNotUse': 'This profile doesn’t use your company’s brand.',
  'appearance.brand.useCompanyBrand': 'Use my company’s brand',
  'appearance.brand.promo': 'Want the same style across all your profiles? Set up your company brand once and it’ll apply automatically to new profiles.',
  'appearance.brand.goToBrand': 'Go to Brand',
  'appearance.lockedByBrand': 'Set by your company’s brand',
  'appearance.template.title': 'Template',
  'appearance.template.classic': 'Classic',
  'appearance.template.classicDesc': 'Immersive background with a gradient of your main color',
  'appearance.template.corporate': 'Corporate',
  'appearance.template.corporateDesc': 'Solid background and banner, with your own background and text colors',
  'appearance.template.portrait': 'Portrait',
  'appearance.template.portraitDesc': 'Your photo full-page, with a color panel below',
  'appearance.watermark.title': 'Watermark',
  'appearance.watermark.disabledTitle': 'Contact watermark disabled',
  'appearance.watermark.disabledSub': 'Your public profile doesn’t show the Contact logo.',
  'appearance.watermark.requiresPaid': 'Requires a paid plan',
  'appearance.watermark.removeButton': 'Remove Contact watermark',
  'appearance.watermark.upgradeHint': 'Upgrade to a paid plan to remove the Contact logo from your public profile.',
  'appearance.color.title': 'Color',
  'appearance.color.custom': 'Custom',
  'appearance.color.customDesc': 'Pick your own colors',
  'appearance.color.mainButtonColor': 'Main Button Color',
  'appearance.color.mainButtonColorHint': 'This color automatically generates the rest of the palette',
  'appearance.color.paletteTitle': 'Automatically Generated Palette',
  'appearance.color.applySuggestions': 'Apply suggestions',
  'appearance.color.primary': 'Primary',
  'appearance.color.secondary': 'Secondary',
  'appearance.color.accent': 'Accent',
  'appearance.color.textContrast': 'Text contrast',
  'appearance.color.lowContrast': 'Low contrast',
  'appearance.color.backgroundColor': 'Background Color',
  'appearance.color.textColor': 'Text Color',
  'appearance.color.contrastWarning': 'The contrast between text and background may be hard to read',
  'appearance.color.classicNote': 'The Classic template uses an automatic gradient based on your main color — background and text aren’t customizable in this mode. Switch to Corporate or Portrait to set them manually.',
  'appearance.preview.title': 'Preview',
  'appearance.preview.unsaved': 'Unsaved changes — this is how your profile would look',
  'appearance.preview.current': 'This is how your profile looks now',

  // ── BrandKitEditor.js ────────────────────────────────────────────────
  'brandKit.toast.saved': 'Your company’s brand saved successfully',
  'brandKit.toast.savedShort': 'Company brand saved',
  'brandKit.toast.saveError': 'Couldn’t save the brand',
  'brandKit.title': 'Your Company’s Brand',
  'brandKit.loading': 'Loading...',
  'brandKit.subtitle': 'It will apply to your team’s new profiles.',
  'brandKit.template.classicDesc': 'Immersive background with a gradient of the main color',
  'brandKit.template.portraitDesc': 'Full-page photo, with a color panel below',
  'brandKit.color.classicNote': 'The Classic template uses an automatic gradient based on the main color. Background and text aren’t customizable in this mode.',
  'brandKit.preview.desc': 'This is how a profile would look with this brand applied.',
  'brandKit.applyModal.title': 'Which profiles should use your brand?',
  'brandKit.applyModal.desc': 'This replaces the current template and colors on any profiles you leave checked. It can’t be undone automatically.',
  'brandKit.applyModal.selectAll': 'Select all',
  'brandKit.applyModal.deselectAll': 'Deselect all',
  'brandKit.applyModal.searchPlaceholder': 'Search profile...',
  'brandKit.applyModal.noMatches': 'No profile matches your search.',
  'brandKit.applyModal.cancel': 'Cancel',
  'brandKit.applyModal.applying': 'Applying...',
  'brandKit.applyModal.applyTo': 'Apply to {selected} of {total} profiles',

  // ── HubPanel.js ──────────────────────────────────────────────────────
  'hub.status.new': 'New',
  'hub.status.pending_contact': 'Pending contact',
  'hub.status.contacted': 'Contacted',
  'hub.status.qualified': 'Qualified',
  'hub.status.meeting_scheduled': 'Meeting scheduled',
  'hub.status.proposal_sent': 'Proposal sent',
  'hub.status.won': 'Won',
  'hub.status.lost': 'Lost',
  'hub.interest.low': 'Low',
  'hub.interest.medium': 'Medium',
  'hub.interest.high': 'High',
  'hub.source.public_form': 'Public form',
  'hub.source.manual': 'Manual',
  'hub.source.scan': 'Scanned',
  'hub.source.import': 'Imported',
  'hub.source.fromLead': 'From Lead',
  'hub.outcome.pending': 'Pending',
  'hub.outcome.won': 'Won',
  'hub.outcome.lost': 'Lost',
  'hub.error.sessionExpired': 'Your session expired. Reload the page and sign in again.',
  'hub.error.noAccess': 'You don’t have access to this section.',
  'hub.error.loadFailed': 'Couldn’t load contacts. Please try again.',
  'hub.toast.scanned': 'Card scanned. Review the details before saving.',
  'hub.toast.scanError': 'Couldn’t scan the card. Please try again.',
  'hub.toast.nameRequired': 'Name is required',
  'hub.toast.contactAdded': 'Contact added',
  'hub.toast.addError': 'Couldn’t add the contact',
  'hub.confirm.unsavedExit': 'You have unsaved changes on this card. Exit anyway?',
  'hub.confirm.unsavedExitLabel': 'Exit without saving',
  'hub.toast.contactUpdated': 'Contact updated',
  'hub.toast.syncError': 'The contact was saved, but couldn’t sync with Hub.',
  'hub.toast.saveError': 'Couldn’t save the contact',
  'hub.confirm.deleteLegacy': 'Delete this contact? It’s a legacy record and this action can’t be undone.',
  'hub.confirm.deleteLegacyLabel': 'Delete contact',
  'hub.toast.deleted': 'Contact deleted',
  'hub.toast.deleteError': 'Couldn’t delete the contact',
  'hub.toast.archived': 'Contact archived. Bring it back with "Show archived".',
  'hub.toast.restored': 'Contact restored',
  'hub.toast.undoError': 'Couldn’t undo. Restore the contact manually.',
  'hub.toast.archiveError': 'Couldn’t archive the contact',
  'hub.title': 'Contact Hub',
  'hub.subtitle': 'Manage and follow up on your business contacts.',
  'hub.scanning': 'Scanning…',
  'hub.scanCard': 'Scan card',
  'hub.scanCardAria': 'Scan business card',
  'hub.addContact': 'Add contact',
  'hub.metricsAria': 'Contact metrics',
  'hub.metrics.total': 'Total contacts',
  'hub.metrics.new': 'New',
  'hub.metrics.pending': 'Pending contact',
  'hub.metrics.pendingActions': 'Pending actions',
  'hub.metrics.cappedNote': 'New, pending, and pending actions are calculated over the first 200 loaded contacts. The total is exact.',
  'hub.filters.searchPlaceholder': 'Search by name, email, phone, or company',
  'hub.filters.searchAria': 'Search contacts',
  'hub.filters.statusAria': 'Filter by status',
  'hub.filters.allStatuses': 'All statuses',
  'hub.filters.sourceAria': 'Filter by source',
  'hub.filters.allSources': 'All sources',
  'hub.filters.interestAria': 'Filter by interest level',
  'hub.filters.allLevels': 'All levels',
  'hub.filters.showArchived': 'Show archived',
  'hub.filters.clear': 'Clear filters',
  'hub.retry': 'Retry',
  'hub.empty.noContactsTitle': 'No contacts yet',
  'hub.empty.noContactsDesc': 'Start by adding your first business contacts.',
  'hub.empty.addFirst': 'Add first contact',
  'hub.empty.noMatchesTitle': 'No contacts found',
  'hub.empty.noMatchesDesc': 'Try adjusting your search or applied filters.',
  'hub.table.contact': 'Contact',
  'hub.table.info': 'Info',
  'hub.table.status': 'Status',
  'hub.table.interest': 'Interest',
  'hub.table.nextAction': 'Next action',
  'hub.table.source': 'Source',
  'hub.table.updated': 'Updated',
  'hub.viewCard': 'View {name}’s card',
  'hub.noData': 'No data',
  'hub.undefined': 'Not set',
  'hub.noAction': 'No action',
  'hub.overdue': 'Overdue',
  'hub.today': 'Today',
  'hub.call': 'Call',
  'hub.sendEmailAria': 'Send email',
  'hub.email': 'Email',
  'hub.openWhatsAppAria': 'Open WhatsApp',
  'hub.legacy.title': 'Legacy contacts',
  'hub.legacy.desc': 'These records come from a previous version of the Hub and have limited functionality.',
  'hub.addModal.title': 'Add Contact',
  'hub.addModal.closeAria': 'Close form',
  'hub.field.fullName': 'Full name',
  'hub.field.fullNamePlaceholder': 'John Smith',
  'hub.field.company': 'Company',
  'hub.field.position': 'Position',
  'hub.field.email': 'Email',
  'hub.field.phone': 'Phone',
  'hub.field.tags': 'Tags',
  'hub.field.tagsPlaceholder': 'Add a tag (e.g.: Client, VIP)',
  'hub.field.removeTagAria': 'Remove tag {tag}',
  'hub.field.campaign': 'Campaign',
  'hub.field.noCampaign': 'No campaign',
  'hub.field.notes': 'Notes',
  'hub.field.notesPlaceholder': 'Additional info…',
  'hub.saving': 'Saving…',
  'hub.addModal.save': 'Save contact',
  'hub.cancel': 'Cancel',
  'hub.drawer.aria': '{name}’s card',
  'hub.drawer.created': 'Created',
  'hub.drawer.closeAria': 'Close contact card',
  'hub.drawer.contactSection': 'Contact',
  'hub.send': 'Send',
  'hub.drawer.noContactInfo': 'No contact info available.',
  'hub.drawer.dealSection': 'Deal status',
  'hub.field.interestLevel': 'Interest level',
  'hub.drawer.nextActionSection': 'Next action',
  'hub.field.whatToDo': 'What to do',
  'hub.field.whatToDoPlaceholder': 'E.g.: call to close proposal',
  'hub.field.when': 'When',
  'hub.drawer.actionCaption': 'Manual planning only — doesn’t generate reminders or automatic notifications.',
  'hub.drawer.meetingSection': 'Meeting',
  'hub.field.dateTimeOptional': 'Date and time (optional)',
  'hub.drawer.meetingCaption': 'Only recorded here — doesn’t create calendar events.',
  'hub.drawer.outcomeSection': 'Outcome',
  'hub.field.reason': 'Reason',
  'hub.field.reasonPlaceholder': 'Reason for the outcome…',
  'hub.drawer.notesPlaceholder': 'Additional info about this contact…',
  'hub.drawer.notesAria': 'Contact notes',
  'hub.field.addTagPlaceholder': 'Add tag',
  'hub.drawer.infoSection': 'Info',
  'hub.drawer.updated': 'Updated',
  'hub.drawer.archived': 'Archived',
  'hub.save': 'Save',
  'hub.archiveContact': 'Archive contact',

  // ── CampaignsPanel.js ────────────────────────────────────────────────
  'campaigns.status.scheduled': 'Scheduled',
  'campaigns.status.active': 'Active',
  'campaigns.status.ended': 'Ended',
  'campaigns.toast.loadError': 'Couldn’t load the event',
  'campaigns.toast.contactsLoadError': 'Couldn’t load the contacts',
  'campaigns.toast.endBeforeStart': 'The end date can’t be before the start date.',
  'campaigns.toast.created': 'Event created',
  'campaigns.toast.createError': 'Couldn’t create the event',
  'campaigns.toast.activated': 'Event activated',
  'campaigns.toast.ended': 'Event ended',
  'campaigns.toast.updateError': 'Couldn’t update the event',
  'campaigns.toast.budgetUpdated': 'Budget updated',
  'campaigns.toast.budgetError': 'Couldn’t update the budget',
  'campaigns.confirm.delete': 'Delete "{name}"? This action can’t be undone.',
  'campaigns.confirm.deleteTitle': 'Delete event',
  'campaigns.confirm.deleteLabel': 'Delete',
  'campaigns.toast.deleted': 'Event deleted',
  'campaigns.toast.deleteError': 'Couldn’t delete the event',
  'campaigns.exportFilename': 'Event',
  'campaigns.toast.exportError': 'Couldn’t export the event',
  'campaigns.loading': 'Loading…',
  'campaigns.noContacts': 'No contacts.',
  'campaigns.noAdditionalData': 'No additional data',
  'campaigns.first200': 'Showing the first 200 contacts.',
  'campaigns.title': 'Events',
  'campaigns.subtitle': 'Group your leads by event or trade show and measure how it went.',
  'campaigns.hide': 'Hide',
  'campaigns.howItWorks': 'How does it work?',
  'campaigns.createEvent': 'Create event',
  'campaigns.howItWorks2': 'How it works',
  'campaigns.hiw.step1Title': 'Before the event',
  'campaigns.hiw.step1Text': 'Create an event with a name, dates, and the profile you’ll use at the booth.',
  'campaigns.hiw.step2Title': 'During the event',
  'campaigns.hiw.step2Text': 'Every time someone scans your card and fills out the form, the lead gets linked to the event automatically — the visitor won’t notice anything different.',
  'campaigns.hiw.step3Title': 'Got a physical business card?',
  'campaigns.hiw.step3Text': 'Add it manually from Hub — if there’s an active event, it’s pre-selected automatically.',
  'campaigns.hiw.step4Title': 'After the event',
  'campaigns.hiw.step4Text': 'End the event and review the full funnel: how many leads, how many qualified, how many were won.',
  'campaigns.hiw.realExample': 'Real example',
  'campaigns.empty.title': 'No events yet',
  'campaigns.empty.desc': 'Create one for your next event and measure how many leads you captured there. Not sure how it works? Check the button above.',
  'campaigns.leadSingular': 'lead',
  'campaigns.leadPlural': 'leads',
  'campaigns.activate': 'Activate',
  'campaigns.end': 'End',
  'campaigns.editBudget': 'Edit budget',
  'campaigns.budget': 'Budget',
  'campaigns.delete': 'Delete',
  'campaigns.totalLeads': 'Total leads',
  'campaigns.costPerLead': 'Cost per lead',
  'campaigns.costPerQualifiedLead': 'Cost per qualified lead',
  'campaigns.addBudgetHint': 'Add the event’s budget to see the cost per lead.',
  'campaigns.add': 'Add',
  'campaigns.funnel': 'Funnel',
  'campaigns.noLeadsYet': 'No leads in this event yet.',
  'campaigns.byTeamMember': 'By team member',
  'campaigns.qualified': 'qualified',
  'campaigns.newEvent': 'New event',
  'campaigns.form.name': 'Name',
  'campaigns.form.startDate': 'Start date',
  'campaigns.form.endDate': 'End date',
  'campaigns.form.budgetOptional': 'Event budget (CLP, optional)',
  'campaigns.form.budgetPlaceholder': 'E.g.: 500000',
  'campaigns.form.profilesToUse': 'Profiles that will be used',
  'campaigns.creating': 'Creating…',
  'campaigns.budgetModal.title': 'Event Budget',
  'campaigns.budgetModal.desc': 'Used to calculate the cost per lead and per qualified lead.',
  'campaigns.budgetModal.amount': 'Amount (CLP)',

  // FollowUpsPanel.js
  'followups.step.connection.name': 'Connection',
  'followups.step.followup.name': 'Follow-up',
  'followups.step.value.name': 'Value add',
  'followups.delay.0': 'Immediately',
  'followups.delay.15': '15 minutes',
  'followups.delay.30': '30 minutes',
  'followups.delay.60': '1 hour',
  'followups.delay.180': '3 hours',
  'followups.delay.1440': '1 day',
  'followups.seqStatus.active': 'Following up',
  'followups.seqStatus.responded': 'Responded',
  'followups.seqStatus.completed': 'Completed',
  'followups.seqStatus.finished': 'Completed',
  'followups.seqStatus.cancelled': 'Cancelled',
  'followups.seqStatus.failed': 'Failed',
  'followups.stepStatus.scheduled': 'Scheduled',
  'followups.stepStatus.sent': 'Sent',
  'followups.stepStatus.delivered': 'Delivered',
  'followups.stepStatus.cancelled': 'Cancelled',
  'followups.stepStatus.skipped': 'Skipped',
  'followups.stepStatus.failed': 'Failed',
  'followups.stepStatus.bounced': 'Bounced',
  'followups.filter.all': 'All',
  'followups.filter.active': 'Following up',
  'followups.filter.responded': 'Responded',
  'followups.filter.completed': 'Completed',
  'followups.filter.cancelled': 'Cancelled',
  'followups.filter.failed': 'Failed',
  'followups.timeAgo.now': 'now',
  'followups.timeAgo.minutes': '{n}m ago',
  'followups.timeAgo.hours': '{n}h ago',
  'followups.timeAgo.yesterday': 'yesterday',
  'followups.timeAgo.days': '{n}d ago',
  'followups.stepProgress': '{done} of {total}',
  'followups.toast.loadError': 'We couldn’t load your Follow-ups settings.',
  'followups.toast.needOnboarding': 'Finish the initial setup to activate Follow-ups.',
  'followups.toast.activated': 'Automatic follow-up activated.',
  'followups.toast.paused': 'Automatic follow-up paused.',
  'followups.toast.statusError': 'We couldn’t update the status.',
  'followups.toast.stepActivated': '{step} activated.',
  'followups.toast.stepDeactivated': '{step} deactivated.',
  'followups.toast.stepUpdateError': 'We couldn’t update the step.',
  'followups.toast.saved': 'Changes saved.',
  'followups.toast.saveError': 'We couldn’t save your changes.',
  'followups.confirm.restoreMessage': 'Restore the recommended message for {step}? This replaces the current subject and message.',
  'followups.confirm.restoreMessageTitle': 'Restore recommended message',
  'followups.confirm.restoreMessageConfirm': 'Restore',
  'followups.toast.restored': 'Recommended message restored — remember to save your changes.',
  'followups.toast.restoreError': 'We couldn’t restore the recommended message.',
  'followups.toast.testSent': 'Test email sent — check your inbox.',
  'followups.toast.testError': 'We couldn’t send the test.',
  'followups.toast.fileTooBig': 'The file can’t be larger than 8MB.',
  'followups.toast.attached': 'File attached.',
  'followups.toast.attachError': 'We couldn’t upload the file.',
  'followups.toast.attachRemoved': 'Attachment removed.',
  'followups.toast.attachRemoveError': 'We couldn’t remove the attachment.',
  'followups.confirm.stopSequence': 'Stop the follow-up for {name}? Any upcoming scheduled emails will be cancelled.',
  'followups.confirm.stopSequenceTitle': 'Stop follow-up',
  'followups.confirm.stopSequenceConfirm': 'Stop follow-up',
  'followups.toast.sequenceStopped': 'Follow-up stopped.',
  'followups.toast.sequenceStopError': 'We couldn’t stop the follow-up.',
  'followups.stepPill.inactive': 'Inactive',
  'followups.stepPill.pending': 'Pending config.',
  'followups.stepPill.active': 'Active',
  'followups.stepWhen.day': 'Day {n} if no reply',
  'followups.preview.you': 'You',
  'followups.preview.youLower': 'you',
  'followups.preview.yourName': 'Your name',
  'followups.preview.profileUrl': 'contactnetwork.cl/pages/your-profile',
  'followups.loading': 'Loading Follow-ups…',
  'followups.loadErrorMsg': 'We couldn’t load your Follow-ups settings.',
  'followups.retry': 'Retry',
  'followups.custom.step1.title': 'Make your emails feel like you',
  'followups.custom.step2.title': 'Define how you want to follow up',
  'followups.custom.step3.title': 'Prepare your messages',
  'followups.custom.step4.title': 'You’re all set',
  'followups.custom.step1.sub': 'This is how people will see you when they receive your follow-ups.',
  'followups.custom.step2.sub': 'Choose when CONTACT should write again if you still haven’t received a reply.',
  'followups.custom.step3.sub': 'You can start with our recommended messages and edit them anytime.',
  'followups.custom.step4.sub': 'Review your setup before activating Follow-ups.',
  'followups.custom.name': 'Name',
  'followups.custom.sentAs': 'Your emails will be sent as',
  'followups.custom.repliesTo': 'Replies will go straight to',
  'followups.custom.emailPlaceholder': 'you@email.com',
  'followups.custom.step1.explain': 'People will be able to reply to you directly from their email. If you use a different address to read your mail, you can change it here.',
  'followups.preview.from': 'From:',
  'followups.preview.to': 'To:',
  'followups.custom.daysAfter': 'days after',
  'followups.custom.deactivate': 'Deactivate',
  'followups.custom.activate': 'Activate',
  'followups.custom.step2.explain': 'If the person replies at any point, CONTACT automatically cancels the following emails.',
  'followups.field.subject': 'Subject',
  'followups.field.message': 'Message',
  'followups.custom.useRecommended': 'Use recommended',
  'followups.custom.customize': 'Customize',
  'followups.custom.check.identity': 'Identity configured —',
  'followups.custom.check.sentFrom': 'Your emails will be sent from',
  'followups.custom.check.repliesTo': 'Replies will go to',
  'followups.custom.sequence': 'Sequence',
  'followups.custom.check.emailsReady': '{n} emails ready',
  'followups.custom.step4.explain': 'The sequence will stop automatically as soon as someone replies.',
  'followups.finishLater': 'Finish later',
  'followups.landing.title': 'Set up your Follow-ups',
  'followups.landing.sub': 'Follow up automatically after every new connection.',
  'followups.landing.flowNote': 'If a person replies, CONTACT automatically stops the following emails.',
  'followups.landing.recommended': 'Recommended',
  'followups.landing.recoTitle': 'Get started in under a minute',
  'followups.landing.recoDesc': 'Use a sequence prepared by CONTACT and start following up without configuring everything from scratch.',
  'followups.landing.day3': 'Day 3 if no reply',
  'followups.landing.day6': 'Day 6 if no reply',
  'followups.landing.useRecommended': 'Use recommended setup',
  'followups.landing.customTitle': 'Set it up your way',
  'followups.landing.customDesc': 'Customize the timing and messages before activating your follow-up.',
  'followups.landing.customCta': 'Customize Follow-ups',
  'followups.reco.title': 'Your Follow-up is ready',
  'followups.reco.identity': 'Identity',
  'followups.reco.messages': 'Messages',
  'followups.reco.messagesReady': '3 recommended messages ready',
  'followups.reco.explain': 'Upcoming emails will be cancelled automatically as soon as a person replies.',
  'followups.reco.activate': 'Activate Follow-ups',
  'followups.reco.sendTest': 'Send a test',
  'followups.back': '← Back',
  'followups.custom.stepOf': 'Step {n} of 4',
  'followups.custom.back': 'Back',
  'followups.custom.continue': 'Continue',
  'followups.success.title': 'Follow-ups is active',
  'followups.success.sub': 'Your next connection will get automatic follow-up.',
  'followups.success.goTo': 'Go to Follow-ups',
  'followups.howItWorks.title': 'How it works',
  'followups.howItWorks.step1.title': 'New connection',
  'followups.howItWorks.step1.desc': 'A person shares their details with you.',
  'followups.howItWorks.step2.title': 'Automatic follow-up',
  'followups.howItWorks.step2.desc': 'CONTACT sends your sequence based on the timing you configured.',
  'followups.howItWorks.step3.title': 'Stops when they reply',
  'followups.howItWorks.step3.desc': 'If the person replies, the following emails are cancelled automatically.',
  'followups.howItWorks.note': 'Follow-ups starts when a real connection is generated (public form or scan) — not simply because someone views your profile or opens a link.',
  'followups.howItWorks.understood': 'Got it',
  'followups.header.sub': 'Automate follow-up after every new connection.',
  'followups.tabs.sequence': 'Sequence',
  'followups.tabs.activity': 'Activity',
  'followups.notConfiguredTitle': 'Follow-ups isn’t configured yet',
  'followups.paused': 'Paused',
  'followups.notConfiguredBanner': 'Follow-ups isn’t configured on the platform yet. Contact us to activate it.',
  'followups.activeNote': 'New connections are automatically entering this sequence.',
  'followups.pausedNote': 'New connections won’t receive automatic follow-up.',
  'followups.replyWarning': 'Email 2 and/or Email 3 are activated, but they’ll be sent as soon as we finish enabling reply detection. In the meantime they stay saved, unsent.',
  'followups.metric.started': 'Follow-ups started',
  'followups.metric.responded': 'Responded',
  'followups.metric.responseRate': 'Response rate',
  'followups.metric.active': 'Following up',
  'followups.sender.identity': 'Sending identity',
  'followups.sender.identityTip': 'Your contacts will see your name and their replies will go straight to this email.',
  'followups.timeline.title': 'Your sequence',
  'followups.timeline.foot': 'The sequence stops automatically once the contact replies.',
  'followups.edit.title': 'Edit {step}',
  'followups.edit.unsaved': 'Unsaved changes',
  'followups.edit.saved': 'Saved',
  'followups.edit.desc.connection': 'Sent after the connection, with the wait time you choose.',
  'followups.edit.desc.followup': 'Only sent if the person hasn’t replied to you yet.',
  'followups.edit.desc.value': 'Closes the sequence — you can attach a file or share a link.',
  'followups.edit.pendingReplyConfig': 'Will activate automatically once we finish setting up reply detection.',
  'followups.edit.sendAfter': 'Send after',
  'followups.edit.sendOnDay': 'Send on day (from Email 1)',
  'followups.field.subjectPlaceholder': 'Great to meet you, {{nombre}}',
  'followups.restoreLink': 'Restore recommended message',
  'followups.field.attachment': 'Attachment or resource',
  'followups.uploading': 'Uploading…',
  'followups.attachHint': 'Click to attach a file (max. 8MB)',
  'followups.or': 'or',
  'followups.field.resourceLinkPlaceholder': 'https://your-resource.com',
  'followups.field.valueResource': 'Value resource',
  'followups.saveChanges': 'Save changes',
  'followups.sending': 'Sending…',
  'followups.testSentBtn': 'Test sent',
  'followups.testErrorBtn': 'Couldn’t send',
  'followups.sendTestBtn': 'Send test',
  'followups.preview.title': 'Preview',
  'followups.preview.desc': 'With sample data, this is how it would look in your contact’s inbox.',
  'followups.preview.subject': 'Subject:',
  'followups.recent.title': 'Recent activity',
  'followups.recent.desc': 'What has happened with each sequence sent.',
  'followups.empty.title': 'You haven’t started any follow-ups yet',
  'followups.empty.text': 'Once you capture your next connection, its sequence will start automatically.',
  'followups.viewAllActivity': 'View all activity',
  'followups.activity.loadError': 'We couldn’t load the activity.',
  'followups.activity.searchPlaceholder': 'Search contact...',
  'followups.activity.noSearchResults': 'No search results',
  'followups.activity.noSearchResultsText': 'We couldn’t find any contacts for "{query}".',
  'followups.activity.noResults': 'No results',
  'followups.activity.noResultsText': 'We couldn’t find any follow-ups with this status.',
  'followups.table.contact': 'Contact',
  'followups.table.status': 'Status',
  'followups.table.progress': 'Progress',
  'followups.table.nextStep': 'Next step',
  'followups.table.lastActivity': 'Last activity',
  'followups.pagination.range': '{start}–{end} of {total}',
  'followups.pagination.prev': 'Previous',
  'followups.pagination.next': 'Next',
  'followups.drawer.note.respondedBefore': 'Replied before it was sent',
  'followups.drawer.note.stopped': 'Follow-up stopped',
  'followups.drawer.note.wasNotActive': 'Wasn’t active',
  'followups.drawer.note.failed': 'Couldn’t be delivered',
  'followups.drawer.note.bounced': 'The email bounced',
  'followups.drawer.responseReceived': 'Reply received',
  'followups.drawer.nextSend': 'Next send',
  'followups.drawer.stopSequence': 'Stop follow-up',

  // DeveloperPanel.js
  'developer.status.active': 'Active',
  'developer.status.revoked': 'Revoked',
  'developer.status.paused': 'Paused',
  'developer.status.expired': 'Expired',
  'developer.copy': 'Copy',
  'developer.close': 'Close',
  'developer.expiry.never': 'Never expires',
  'developer.expiry.30': '30 days',
  'developer.expiry.60': '60 days',
  'developer.expiry.90': '90 days',
  'developer.apiKeys.title': 'API Keys',
  'developer.apiKeys.activeCount': '{n} active',
  'developer.new': 'New',
  'developer.newMasculine': 'New',
  'developer.apiKeys.newTitle': 'New API key',
  'developer.name': 'Name',
  'developer.apiKeys.namePlaceholder': 'e.g. Internal CRM',
  'developer.expiration': 'Expiration',
  'developer.apiKeys.sandboxMode': 'Sandbox mode (returns test data, never your real leads)',
  'developer.create': 'Create',
  'developer.cancel': 'Cancel',
  'developer.apiKeys.copyNow': 'Copy your key now — you won’t see it again',
  'developer.apiKeys.sandboxNote': 'Sandbox key — only returns test data',
  'developer.apiKeys.expiresOn': 'Expires on {date}',
  'developer.apiKeys.empty': 'No keys yet',
  'developer.apiKeys.expiresShort': 'Expires {date}',
  'developer.revoke': 'Revoke',
  'developer.apiKeys.deleteFromList': 'Delete from list',
  'developer.viewActivity': 'View activity',
  'developer.usageWindow': 'Usage (current window)',
  'developer.recentActivity': 'Recent activity',
  'developer.noCallsYet': 'No calls yet.',
  'developer.rateLimit.leads': 'Leads',
  'developer.rateLimit.analytics_summary': 'Analytics',
  'developer.delivery.delivered': 'Delivered',
  'developer.delivery.failed': 'Failed',
  'developer.delivery.retry_scheduled': 'Retrying',
  'developer.delivery.pending': 'Pending',
  'developer.delivery.processing': 'Processing',
  'developer.delivery.cancelled': 'Cancelled',
  'developer.generating': 'Generating…',
  'developer.regenerateSecret': 'Regenerate secret',
  'developer.recentDeliveries': 'Recent deliveries',
  'developer.noDeliveriesYet': 'No deliveries yet.',
  'developer.attempts': '{n} attempt(s)',
  'developer.retry': 'Retry',
  'developer.webhooks.activeCount': '{n} active',
  'developer.webhooks.newTitle': 'New webhook',
  'developer.webhooks.urlPlaceholder': 'https://your-system.com/webhook',
  'developer.webhooks.copySecretNow': 'Copy the secret now — you won’t see it again',
  'developer.webhooks.empty': 'No webhooks yet',
  'developer.test': 'Test',
  'developer.sendTest': 'Send test',
  'developer.pause': 'Pause',
  'developer.reactivate': 'Reactivate',
  'developer.delete': 'Delete',
  'developer.viewDeliveries': 'View deliveries',
  'developer.hide': 'Hide',
  'developer.tryIt': 'Try it',
  'developer.loadingEllipsis': 'Loading…',
  'developer.docs.title': 'Documentation',
  'developer.docs.auth': 'Authentication',
  'developer.docs.endpointsLimits': 'Endpoints and limits',
  'developer.docs.webhookPayload': 'Webhook payload — "lead.created" event',
  'developer.docs.verifySignature': 'Verify the signature (X-CONTACT-Signature)',
  'developer.docs.possibleErrors': 'Possible errors',
  'developer.endpoint.leads.desc': 'Lists your leads.',
  'developer.endpoint.analyticsSummary.desc': 'Visits, clicks and leads per profile.',
  'developer.endpoint.ping.desc': 'Health check for monitoring — doesn’t use up the other endpoints’ quota.',
  'developer.error.missingKey.message': 'Missing API key (Authorization: Bearer <key>)',
  'developer.error.missingKey.cause': 'The Authorization header wasn’t sent.',
  'developer.error.invalidKey.message': 'Invalid API key',
  'developer.error.invalidKey.cause': 'The key doesn’t exist or doesn’t match any active key.',
  'developer.error.expiredKey.message': 'This API key has expired',
  'developer.error.expiredKey.cause': 'The key has an expiration date that has already passed.',
  'developer.error.planNoApi.message': 'This account’s plan no longer includes API access',
  'developer.error.planNoApi.cause': 'The account downgraded to a plan that no longer includes API access after the key was created.',
  'developer.error.profileNotFound.message': 'Profile not found',
  'developer.error.profileNotFound.cause': 'The submitted profile_id doesn’t belong to this account.',
  'developer.error.rateLimitExceeded.message': 'Request limit exceeded',
  'developer.error.rateLimitExceeded.cause': 'The per-minute request limit for that endpoint was exceeded.',
  'developer.locked.title': 'Connect your own systems',
  'developer.locked.sub': 'Generate API keys and receive webhooks to sync your leads with your CRM, BI tool, or automations.',
  'developer.locked.desc': 'Read access to your leads and analytics, plus automatic webhook deliveries.',
  'developer.locked.exclusivePro': 'Pro exclusive',
  'developer.locked.newKey': 'New key',
  'developer.locked.upgradePlan': 'Upgrade plan',
  'developer.pageTitle': 'Developer',
  'developer.pageSub': 'Connect CONTACT with your own systems through API keys and webhooks.',
  'developer.error': 'error',
  'developer.confirm.revokeKey': 'Revoke the key "{name}"? Any system using it will stop working immediately.',
  'developer.confirm.deleteKey': 'Delete the key "{name}" from the list? This action can’t be undone.',
  'developer.confirm.deleteWebhook': 'Delete the webhook to "{url}"? This action can’t be undone.',
  'developer.confirm.rollSecret': 'The previous secret will stop being valid immediately. Regenerate anyway?',
  'developer.regenerate': 'Regenerate',
  'developer.toast.keysLoadError': 'We couldn’t load the API keys.',
  'developer.toast.keyNeedsName': 'Give the key a name.',
  'developer.toast.keyCreateError': 'We couldn’t create the key.',
  'developer.toast.keyRevoked': 'Key revoked.',
  'developer.toast.keyRevokeError': 'We couldn’t revoke the key.',
  'developer.toast.keyDeleted': 'Key deleted.',
  'developer.toast.keyDeleteError': 'We couldn’t delete the key.',
  'developer.toast.webhooksLoadError': 'We couldn’t load the webhooks.',
  'developer.toast.webhookNeedsUrl': 'Enter the URL that will receive the events.',
  'developer.toast.webhookCreateError': 'We couldn’t create the webhook.',
  'developer.toast.webhookPaused': 'Webhook paused.',
  'developer.toast.webhookReactivated': 'Webhook reactivated.',
  'developer.toast.webhookUpdateError': 'We couldn’t update the webhook.',
  'developer.toast.webhookDeleted': 'Webhook deleted.',
  'developer.toast.webhookDeleteError': 'We couldn’t delete the webhook.',
  'developer.toast.webhookTestSent': 'Test sent — the endpoint responded HTTP {status}.',
  'developer.toast.webhookTestFailed': 'The endpoint didn’t respond correctly ({code}).',
  'developer.toast.webhookTestError': 'We couldn’t send the test.',
  'developer.toast.secretRegenerated': 'Secret regenerated.',
  'developer.toast.secretRegenerateError': 'We couldn’t regenerate the secret.',
  'developer.toast.retryScheduled': 'Retry scheduled — it’ll be sent in the next few seconds.',
  'developer.toast.retryError': 'We couldn’t retry the delivery.',
  'developer.toast.endpointTestError': 'We couldn’t test the endpoint.',
  'developer.toast.postmanDownloadError': 'We couldn’t download the collection.',

  // IntegrationsPanel.js
  'integrations.status.active': 'Active',
  'integrations.status.configuring': 'Configuring',
  'integrations.status.paused': 'Paused',
  'integrations.status.requires_attention': 'Needs attention',
  'integrations.status.disconnected': 'Disconnected',
  'integrations.comingSoon': 'Coming soon',
  'integrations.requiresPaidPlan': 'Requires a paid plan',
  'integrations.close': 'Close',
  'integrations.cancel': 'Cancel',
  'integrations.activate': 'Activate',
  'integrations.reactivate': 'Reactivate',
  'integrations.pause': 'Pause',
  'integrations.title': 'Integrations',
  'integrations.subtitle': 'Connect CONTACT with the tools your team uses and automate the flow of new contacts.',
  'integrations.searchPlaceholder': 'Search integrations',
  'integrations.leadSync.sub': 'Sync CONTACT’s contacts with your CRM, marketing tool, and more.',
  'integrations.calendar.sub': 'Add your scheduling links to coordinate meetings as soon as you connect with a lead.',
  'integrations.email.sub': 'Connect your email to keep your conversations with each lead in sync.',
  'integrations.noMatch': 'No integration matches "{query}".',
  'integrations.gsheets.desc': 'Automatically sends CONTACT’s new contacts to a Google Sheets spreadsheet.',
  'integrations.gsheets.accessNote': 'CONTACT only accesses the spreadsheet it creates for your account when you connect — never other spreadsheets or files in your Google Drive.',
  'integrations.gsheets.connectError': 'We couldn’t start the connection with Google.',
  'integrations.gsheets.spreadsheetCreated': 'Spreadsheet created successfully.',
  'integrations.gsheets.spreadsheetCreateError': 'We couldn’t create the spreadsheet.',
  'integrations.gsheets.activated': 'Integration activated.',
  'integrations.gsheets.activateError': 'We couldn’t activate the integration.',
  'integrations.gsheets.paused': 'Integration paused.',
  'integrations.gsheets.pauseError': 'We couldn’t pause the integration.',
  'integrations.gsheets.disconnected': 'Disconnected from Google Sheets.',
  'integrations.gsheets.disconnectError': 'We couldn’t disconnect.',
  'integrations.gsheets.testConnectionError': 'We couldn’t test the connection.',
  'integrations.gsheets.notConnected': 'You haven’t connected your Google account yet.',
  'integrations.gsheets.connectWithGoogle': 'Connect with Google',
  'integrations.gsheets.connectedAs': 'Connected as',
  'integrations.gsheets.noSheetYet': '— you don’t have a spreadsheet yet. CONTACT will create one automatically.',
  'integrations.gsheets.createSpreadsheet': 'Create spreadsheet',
  'integrations.gsheets.openSpreadsheet': 'Open spreadsheet',
  'integrations.gsheets.testConnection': 'Test connection',
  'integrations.gsheets.testRowSent': 'Test row sent successfully.',
  'integrations.gsheets.testFailed': 'The test failed.',
  'integrations.gsheets.lastSync': 'Last sync:',
  'integrations.gsheets.lastError': 'Last error:',
  'integrations.gsheets.disconnectBtn': 'Disconnect Google Sheets',
  'integrations.gsheets.disconnectConfirm': 'Disconnect Google Sheets? You’ll stop receiving new contacts there.',
  'integrations.gsheets.disconnecting': 'Disconnecting…',
  'integrations.gsheets.yesDisconnect': 'Yes, disconnect',
  'integrations.gsheets.accountConnected': 'Your Google account is now connected.',

  // SettingsPanel.js
  'settings.tabs.account': 'Account',
  'settings.tabs.plan': 'Plan & billing',
  'settings.tabs.support': 'Support',
  'settings.tabs.danger': 'Danger zone',
  'settings.language.title': 'Language',
  'settings.language.sub': 'Choose the platform language',
  'settings.memberSince': 'Member since {date}',
  'settings.title': 'Account Settings',
  'settings.subtitle': 'Manage your personal information and security',
  'settings.user': 'User',
  'settings.name.title': 'Username',
  'settings.name.sub': 'This name is shown in your greeting and profile',
  'settings.name.new': 'New name',
  'settings.name.placeholder': 'Enter your name',
  'settings.name.none': 'No name',
  'settings.edit': 'Edit',
  'settings.save': 'Save',
  'settings.saving': 'Saving...',
  'settings.cancel': 'Cancel',
  'settings.close': 'Close',
  'settings.name.empty': 'The name can’t be empty',
  'settings.name.saved': 'Your name was updated successfully.',
  'settings.name.updateError': 'Error updating the name',
  'settings.sessionExpired': 'Your session has expired. Please log in again.',
  'settings.networkError': 'Connection error. Please check your internet and try again.',
  'settings.password.title': 'Password',
  'settings.password.sub': 'Update your login password',
  'settings.password.change': 'Change password',
  'settings.password.current': 'Current password',
  'settings.password.currentPlaceholder': 'Enter your current password',
  'settings.password.currentPlaceholder2': 'Your current password',
  'settings.password.new': 'New password',
  'settings.password.newPlaceholder': 'Enter your new password',
  'settings.password.requirements': 'Minimum 8 characters, include an uppercase letter, a lowercase letter, and a number',
  'settings.password.confirm': 'Confirm new password',
  'settings.password.confirmPlaceholder': 'Confirm your new password',
  'settings.password.update': 'Update password',
  'settings.password.minChars': 'Minimum 8 characters',
  'settings.password.needUpper': 'Must include an uppercase letter',
  'settings.password.needLower': 'Must include a lowercase letter',
  'settings.password.needNumber': 'Must include numbers',
  'settings.password.enterCurrent': 'Enter your current password',
  'settings.password.mismatch': 'Passwords don’t match',
  'settings.password.saved': 'Your password was updated successfully.',
  'settings.password.updateError': 'Error changing the password',
  'settings.integrations.title': 'Integrations',
  'settings.integrations.sub': 'Google Sheets and upcoming CONTACT integrations.',
  'settings.integrations.manage': 'Manage integrations',
  'settings.support.title': 'Support',
  'settings.support.sub': 'Have questions or need help? Contact us directly.',
  'settings.support.talkToStaff': 'Talk to our team',
  'settings.support.sendEmail': 'Send an email',
  'settings.email.label': 'Email:',
  'settings.email.locked': 'The email address can’t be changed',
  'settings.billing.monthly': 'monthly',
  'settings.billing.annual': 'annual',
  'settings.cancelReason.preferNotSay': 'Prefer not to say',
  'settings.cancelReason.price': 'The price',
  'settings.cancelReason.not_using': 'I don’t use it enough',
  'settings.cancelReason.missing_feature': 'Missing a feature I need',
  'settings.cancelReason.alternative': 'I found another alternative',
  'settings.cancelReason.other': 'Another reason',
  'settings.plan.title': 'My Plan',
  'settings.plan.sub': 'Review your current plan and manage your subscription',
  'settings.plan.activatedByTeam': 'Activated by the Contact Network team',
  'settings.plan.subscription': 'Subscription',
  'settings.plan.nextCharge': 'next charge',
  'settings.plan.proEndsOn': 'Your Pro plan ends on',
  'settings.plan.pausedUntil': 'Subscription paused · access until {date}',
  'settings.plan.pausedShort': 'Subscription paused',
  'settings.plan.viewPlans': 'View plans',
  'settings.plan.resuming': 'Resuming…',
  'settings.plan.resumeSubscription': 'Resume subscription',
  'settings.plan.renewPlan': 'Renew my plan',
  'settings.plan.billing': 'Billing',
  'settings.plan.redirecting': 'Redirecting…',
  'settings.plan.switchTo': 'Switch to {period}',
  'settings.plan.profilesUsed': 'Profiles used',
  'settings.plan.paymentHistory': 'Payment history',
  'settings.plan.paymentApproved': 'Approved',
  'settings.plan.paymentRejected': 'Rejected',
  'settings.plan.cancelledUntil': 'Your subscription was cancelled. Your Pro plan will stay active until {date}.',
  'settings.plan.cancelledToFree': 'Your subscription was cancelled. You’re back on the Free plan.',
  'settings.plan.cancelError': 'We couldn’t cancel your subscription. Try again.',
  'settings.plan.paused': 'Your subscription is now paused. You can resume it whenever you want.',
  'settings.plan.pauseError': 'We couldn’t pause your subscription. Try again.',
  'settings.plan.resumed': 'Your subscription was resumed.',
  'settings.plan.resumeError': 'We couldn’t resume your subscription. Try again.',
  'settings.plan.billingSwitchError': 'We couldn’t change your billing. Try again.',
  'settings.plan.cancelConfirmWithDate': 'No more charges will be made. Your Pro plan will stay active until {date}, and then your account will automatically move to the Free plan. Confirm?',
  'settings.plan.cancelConfirmNoDate': 'No more charges will be made. Your account will move to the Free plan as soon as the cancellation is processed. Confirm?',
  'settings.plan.cancelReasonLabel': 'Why are you cancelling? (optional)',
  'settings.plan.editComment': 'Edit comment',
  'settings.plan.addComment': 'Add a comment',
  'settings.plan.cancelling': 'Cancelling…',
  'settings.plan.yesCancel': 'Yes, cancel',
  'settings.plan.noKeep': 'No, keep it',
  'settings.plan.pauseConfirm': 'Your plan will move to Free immediately and no charges will be made while it’s paused. You can resume it whenever you want, without re-entering your card. Confirm?',
  'settings.plan.pausing': 'Pausing…',
  'settings.plan.yesPause': 'Yes, pause',
  'settings.plan.cancelSubscription': 'Cancel subscription',
  'settings.plan.pauseInsteadOfCancel': 'Pause instead of cancelling',
  'settings.plan.tellUsMore': 'Tell us more',
  'settings.plan.optionalHelpsUs': 'Optional. Helps us improve.',
  'settings.plan.commentPlaceholder': 'What would you like us to know?',
  'settings.delete.dangerZone': 'Danger zone',
  'settings.delete.warningPrefix': 'Deleting your account will',
  'settings.delete.permanently': 'permanently',
  'settings.delete.warningSuffix': 'erase all your profiles, leads, analytics, Hub contacts, and settings. This action can’t be undone.',
  'settings.delete.deleteMyAccount': 'Delete my account',
  'settings.delete.confirmTitle': 'Delete your account?',
  'settings.delete.confirmPrefix': 'We’re going to erase all data for',
  'settings.delete.irreversible': 'This action is irreversible.',
  'settings.delete.listProfiles': 'All your public profiles and their URLs',
  'settings.delete.listLeads': 'Your leads, analytics, and Hub contacts',
  'settings.delete.listSubscription': 'Your subscription and account settings',
  'settings.delete.confirmPassword': 'Confirm your password',
  'settings.delete.googleNoPassword': 'If you signed up with Google without a password, leave this field empty.',
  'settings.delete.typePrefix': 'Type',
  'settings.delete.typeSuffix': 'to confirm',
  'settings.delete.deleting': 'Deleting…',
  'settings.delete.deletePermanently': 'Delete permanently',
  'settings.delete.keyword': 'DELETE',
  'settings.delete.typeToConfirm': 'Type "{keyword}" to confirm.',
  'settings.delete.deleteError': 'We couldn’t delete the account.',

  // ProfileEditor.js
  'profileEditor.title': 'Edit Profile',
  'profileEditor.selectProfile': 'Select a profile to edit',
  'profileEditor.saving': 'Saving…',
  'profileEditor.saveChanges': 'Save changes',
  'profileEditor.uploading': 'Uploading…',
  'profileEditor.delete': 'Delete',
  'profileEditor.document': 'Document',
  'profileEditor.requiresPaidPlan': 'Requires a paid plan',
  'profileEditor.toast.saved': 'Profile updated successfully',
  'profileEditor.toast.unknownError': 'Unknown error',
  'profileEditor.toast.saveError': 'We couldn’t save the profile. Try again.',
  'profileEditor.toast.noProfile': 'No profile selected',
  'profileEditor.toast.imageTooBig': 'The image is too large. Maximum 5MB',
  'profileEditor.toast.mustBeImage': 'The file must be an image',
  'profileEditor.toast.imageUpdated': 'Image updated successfully',
  'profileEditor.toast.serverConnectError': 'We couldn’t connect to the server',
  'profileEditor.toast.fileTooBig': 'The file is too large. Maximum 3MB — try compressing it before uploading',
  'profileEditor.toast.fileUploaded': 'File uploaded successfully',
  'profileEditor.toast.fileDeleted': 'File deleted successfully',
  'profileEditor.toast.fileDeleteError': 'We couldn’t delete the file',
  'profileEditor.imageType.profile': 'profile image',
  'profileEditor.imageType.logo': 'company logo',
  'profileEditor.imageType.background': 'background image',
  'profileEditor.imageType.profileCap': 'Profile image',
  'profileEditor.imageType.logoCap': 'Company logo',
  'profileEditor.imageType.backgroundCap': 'Background image',
  'profileEditor.confirm.deleteImage': 'Are you sure you want to delete the {label}?',
  'profileEditor.toast.imageDeleted': '{label} deleted successfully',
  'profileEditor.toast.imageDeleteError': 'Error deleting image: {message}',
  'profileEditor.confirm.deleteFile': 'Are you sure you want to delete this file?',
  'profileEditor.images.title': 'Images',
  'profileEditor.images.profilePhoto': 'Profile Photo',
  'profileEditor.images.profileAlt': 'Profile',
  'profileEditor.images.noImage': 'No image',
  'profileEditor.images.uploadPhoto': 'Upload photo',
  'profileEditor.images.backgroundImage': 'Background Image',
  'profileEditor.images.backgroundAlt': 'Background',
  'profileEditor.images.noBackground': 'No background image',
  'profileEditor.images.uploadBackground': 'Upload background',
  'profileEditor.personal.title': 'Personal Information',
  'profileEditor.personal.name': 'First Name',
  'profileEditor.personal.lastname': 'Last Name',
  'profileEditor.personal.email': 'Email',
  'profileEditor.personal.emailPlaceholder': 'email@example.com',
  'profileEditor.personal.position': 'Job Title / Position',
  'profileEditor.personal.company': 'Company',
  'profileEditor.personal.bio': 'Bio',
  'profileEditor.personal.bioPlaceholder': 'Tell us about yourself and your work…',
  'profileEditor.contact.title': 'Social & Contact',
  'profileEditor.contact.phone': 'Phone (VCF file)',
  'profileEditor.contact.phoneHint': 'Appears in the contact card for direct calls',
  'profileEditor.contact.usernamePlaceholder': '@username',
  'profileEditor.contact.linkedinPlaceholder': 'https://linkedin.com/in/username',
  'profileEditor.contact.facebookPlaceholder': 'https://facebook.com/username',
  'profileEditor.contact.website': 'Website',
  'profileEditor.contact.websitePlaceholder': 'https://my-site.com',
  'profileEditor.links.title': 'Custom Links',
  'profileEditor.links.sub': 'Up to 4 links that will show on your public profile.',
  'profileEditor.links.link': 'Link',
  'profileEditor.links.linkTitle': 'Title',
  'profileEditor.links.placeholder1': 'My Portfolio',
  'profileEditor.links.placeholder2': 'My Catalog',
  'profileEditor.links.placeholder3': 'Services',
  'profileEditor.links.placeholder4': 'Contact',
  'profileEditor.files.title': 'Custom Files',
  'profileEditor.files.sub': 'Up to 2 PDFs or other documents that will appear as buttons on your profile.',
  'profileEditor.files.file': 'File',
  'profileEditor.files.fileName': 'File name',
  'profileEditor.files.buttonName': 'Button name (optional)',
  'profileEditor.files.buttonNameHint': 'If you don’t enter a name, the file’s name is used',
  'profileEditor.files.buttonNamePlaceholder': 'The file’s name will be used',
  'profileEditor.files.uploadFile': 'Upload file',
  'profileEditor.files.uploadHint': 'Maximum 3MB · PDF, DOC, XLS, PPT, ZIP · If it’s bigger, compress it before uploading',
  'profileEditor.files.replaceFile': 'Replace file',

  // ProfilesManager.js
  'profilesManager.home': 'Home',
  'profilesManager.manageAll': 'Manage all your digital profiles ({count}/{max})',
  'profilesManager.creating': 'Creating...',
  'profilesManager.createProfile': 'Create Profile',
  'profilesManager.limitReachedTitle': 'You reached the {max}-profile limit of your plan (deactivated ones count too)',
  'profilesManager.limitReached': 'Profile limit reached · Upgrade plan',
  'profilesManager.stat.activeProfiles': 'Active profiles',
  'profilesManager.stat.totalVisits': 'Total visits',
  'profilesManager.stat.totalClicks': 'Total clicks',
  'profilesManager.stat.leads30d': 'Leads (30 days)',
  'profilesManager.error': 'Error',
  'profilesManager.empty.title': 'You don’t have any profiles yet',
  'profilesManager.empty.sub': 'Create your first digital profile to start sharing your information',
  'profilesManager.table.profiles': 'Profiles',
  'profilesManager.table.visits': 'Visits',
  'profilesManager.table.status': 'Status',
  'profilesManager.table.updated': 'Updated',
  'profilesManager.table.actions': 'Actions',
  'profilesManager.mainBadge': 'Main',
  'profilesManager.noPosition': 'No title',
  'profilesManager.active': 'Active',
  'profilesManager.inactive': 'Inactive',
  'profilesManager.visitsCount': '{n} visits',
  'profilesManager.share': 'Share',
  'profilesManager.copied': 'Copied!',
  'profilesManager.copy': 'Copy',
  'profilesManager.wallet.title': 'Add to Apple Wallet',
  'profilesManager.wallet.sub': 'Carry your CONTACT card on your iPhone, ready to share.',
  'profilesManager.comingSoon': 'Coming soon',
  'profilesManager.slug.changeLink': 'Change Link',
  'profilesManager.slug.warning': 'When you change the link, any card, QR code, or link you already shared with the current one will stop working, and the old link becomes free for someone else to use.',
  'profilesManager.slug.newLink': 'New link',
  'profilesManager.slug.placeholder': 'your-name',
  'profilesManager.cancel': 'Cancel',
  'profilesManager.saving': 'Saving...',
  'profilesManager.saveChanges': 'Save changes',
  'profilesManager.viewProfile': 'View profile',
  'profilesManager.moreActions': 'More actions',
  'profilesManager.mainProfile': 'Main profile',
  'profilesManager.markAsMain': 'Mark as main',
  'profilesManager.changeLink': 'Change link',
  'profilesManager.deactivate': 'Deactivate',
  'profilesManager.activate': 'Activate',
  'profilesManager.deleteProfile': 'Delete profile',
  'profilesManager.confirm.createProfile': 'Are you sure you want to create a new profile?',
  'profilesManager.msg.created': 'New profile created successfully',
  'profilesManager.msg.unknownCreateError': 'Unknown error creating profile',
  'profilesManager.msg.maxProfilesReached': 'You’ve reached the maximum number of profiles. Contact your admin if you need more.',
  'profilesManager.msg.activated': 'Profile activated successfully',
  'profilesManager.toast.deactivated': 'Profile deactivated. It won’t be publicly visible.',
  'profilesManager.toast.reactivated': 'Profile reactivated',
  'profilesManager.toast.undoError': 'We couldn’t undo. Reactivate the profile manually.',
  'profilesManager.msg.deactivateError': 'Error deactivating the profile',
  'profilesManager.msg.activateError': 'Error activating the profile',
  'profilesManager.msg.defaultUpdated': 'Main profile updated successfully',
  'profilesManager.msg.defaultError': 'Error setting main profile',
  'profilesManager.confirm.onlyProfileWarning': 'It’s your only profile: after deleting it, you won’t have any active profiles.',
  'profilesManager.confirm.deleteProfile': 'Delete the profile "{name}"? Its leads, analytics, and saved stats will be lost forever. This action can’t be undone.',
  'profilesManager.noName': 'No name',
  'profilesManager.toast.profileDeleted': 'Profile deleted',
  'profilesManager.toast.deleteError': 'We couldn’t delete the profile',
  'profilesManager.slug.enterValid': 'Enter a valid link',
  'profilesManager.slug.updated': 'Link updated successfully',
  'profilesManager.slug.updateError': 'We couldn’t update the link',
  'profilesManager.greeting.morning': 'Good morning',
  'profilesManager.greeting.afternoon': 'Good afternoon',
  'profilesManager.greeting.evening': 'Good evening',
  'profilesManager.shareTitle': '{name}’s profile',
  'profilesManager.shareText': 'Check out my CONTACT profile',

  // InsightsModule.js
  'insights.title': 'AI Insights',
  'insights.analyzing': 'Analyzing data…',
  'insights.updated': 'Updated',
  'insights.priority.high': 'High',
  'insights.priority.medium': 'Medium',
  'insights.priority.low': 'Low',
  'insights.empty.title': 'No insights available',
  'insights.empty.sub': 'You need more activity to generate recommendations',

  // BeneficiosPanel.js
  'beneficios.loadError': 'Error loading benefits',
  'beneficios.loading': 'Loading benefits...',
  'beneficios.status.sent': 'Sent',
  'beneficios.status.pending': 'Pending',
  'beneficios.status.failed': 'Failed',
  'beneficios.title': 'Benefits',
  'beneficios.subtitle': 'Emails registered for the CONTACT2026 discount code',
  'beneficios.total': 'Total:',
  'beneficios.empty.title': 'No benefits registered yet',
  'beneficios.empty.sub': 'They’ll appear here when someone uses the CONTACT2026 discount code',
  'beneficios.table.email': 'Email',
  'beneficios.table.registeredDate': 'Registration Date',
  'beneficios.table.status': 'Status',
  'beneficios.table.sentDate': 'Sent Date',
  'beneficios.stat.sent': 'Sent',
  'beneficios.stat.pending': 'Pending',
  'beneficios.stat.failed': 'Failed',

  // ImageCropModal.js
  'imageCrop.processError': 'Error processing the image',
  'imageCrop.titleProfile': 'Adjust Profile Image',
  'imageCrop.titleBackground': 'Adjust Background Image',
  'imageCrop.zoom': 'Zoom',
  'imageCrop.instructionsLabel': 'Instructions:',
  'imageCrop.instructionsText': 'Drag the image to center it and use the slider to zoom.',
  'imageCrop.processing': 'Processing...',
  'imageCrop.saveImage': 'Save image',
  'imageCrop.cancel': 'Cancel',

  // TeamAnalyticsPanel.js
  'teamAnalytics.platform.whatsapp': 'WhatsApp',
  'teamAnalytics.platform.instagram': 'Instagram',
  'teamAnalytics.platform.linkedin': 'LinkedIn',
  'teamAnalytics.platform.facebook': 'Facebook',
  'teamAnalytics.platform.tiktok': 'TikTok',
  'teamAnalytics.platform.twitter': 'X / Twitter',
  'teamAnalytics.platform.vcf': 'Saved contact',
  'teamAnalytics.estado.nuevo': 'New',
  'teamAnalytics.estado.contactado': 'Contacted',
  'teamAnalytics.estado.noContactado': 'Not contacted',
  'teamAnalytics.visitsCount': '{n} visits',
  'teamAnalytics.loadError': 'We couldn’t load the team analytics.',
  'teamAnalytics.retry': 'Retry',
  'teamAnalytics.title': 'Team Analytics',
  'teamAnalytics.performanceOfYourProfile': 'Combined performance of your profile',
  'teamAnalytics.performanceOfTeamProfiles': 'Combined performance of your team’s {n} profiles',
  'teamAnalytics.refresh': 'Refresh',
  'teamAnalytics.noProfilesYet': 'You don’t have any profiles in your team yet.',
  'teamAnalytics.stat.profiles': 'Profiles',
  'teamAnalytics.stat.visits': 'Visits',
  'teamAnalytics.stat.clicks': 'Clicks',
  'teamAnalytics.stat.leads': 'Leads',
  'teamAnalytics.stat.saved': '{n} saved',
  'teamAnalytics.visitsLast14Days': 'Visits · last 14 days',
  'teamAnalytics.noVisitsYet': 'No visits recorded yet.',
  'teamAnalytics.clicksByNetwork': 'Clicks by network',
  'teamAnalytics.noClicksYet': 'No clicks recorded yet.',
  'teamAnalytics.leadsByStatus': 'Leads by status',
  'teamAnalytics.totalCount': '{n} total',
  'teamAnalytics.noLeadsYet': 'No leads recorded yet.',
  'teamAnalytics.teamRanking': 'Team ranking',
  'teamAnalytics.profile': 'profile',
  'teamAnalytics.profiles': 'profiles',
  'teamAnalytics.member': 'Member',
  'teamAnalytics.stat.visitsLower': 'visits',
  'teamAnalytics.stat.clicksLower': 'clicks',
  'teamAnalytics.stat.leadsLower': 'leads',
  'teamAnalytics.noMembers': 'No members to show',
  'teamAnalytics.rankingWillAppear': 'The ranking will appear once your team starts generating visits',

  // ProductManagementPanel.js
  'productManagement.toast.selectValidImage': 'Please select a valid image',
  'productManagement.toast.imageMax2MB': 'The image must be smaller than 2MB',
  'productManagement.toast.imageUploadError': 'Error uploading the image',
  'productManagement.toast.nameRequired': 'The product name is required',
  'productManagement.toast.priceMustBePositive': 'The price must be greater than 0',
  'productManagement.toast.productUpdated': 'Product updated successfully',
  'productManagement.toast.saveError': 'Error saving the product',
  'productManagement.toast.invalidPercentage': 'Please enter a valid percentage between 1 and 100',
  'productManagement.toast.codeExists': 'This code already exists',
  'productManagement.toast.selectValidMedia': 'Please select a valid image or video',
  'productManagement.toast.fileMaxSize': 'The file must be smaller than {size}',
  'productManagement.toast.imageUploaded': 'Image uploaded successfully',
  'productManagement.toast.videoUploaded': 'Video uploaded successfully',
  'productManagement.toast.fileUploadError': 'Error uploading the file',
  'productManagement.confirm.deleteFile': 'Are you sure you want to delete this file?',
  'productManagement.delete': 'Delete',
  'productManagement.toast.fileDeleted': 'File deleted successfully',
  'productManagement.toast.fileDeleteError': 'Error deleting the file',
  'productManagement.loading': 'Loading product...',
  'productManagement.title': 'Product Management',
  'productManagement.subtitle': 'Edit the product information shown on the purchase page',
  'productManagement.hidePreview': 'Hide Preview',
  'productManagement.showPreview': 'Show Preview',
  'productManagement.basicInfo': 'Basic Information',
  'productManagement.productName': 'Product Name *',
  'productManagement.productNamePlaceholder': 'E.g.: Contact Smart Card',
  'productManagement.subtitleLabel': 'Subtitle',
  'productManagement.subtitlePlaceholder': 'E.g.: The ultimate tool for modern sales teams',
  'productManagement.shortDescription': 'Short Description',
  'productManagement.shortDescriptionPlaceholder': 'Short product description (2-3 lines)',
  'productManagement.fullDescription': 'Full Description',
  'productManagement.fullDescriptionPlaceholder': 'Detailed product description',
  'productManagement.mainImageSection': 'Product Image (Main)',
  'productManagement.mainImageLabel': 'Main Image (JPG/PNG, max 2MB)',
  'productManagement.preview': 'Preview:',
  'productManagement.mediaGallery': 'Media Gallery',
  'productManagement.mediaGallerySub': 'Add multiple images and videos to show on the product page',
  'productManagement.uploading': 'Uploading...',
  'productManagement.addFile': 'Add File',
  'productManagement.acceptedFormats': 'Accepted formats:',
  'productManagement.images': 'Images:',
  'productManagement.videos': 'Videos:',
  'productManagement.max': 'max',
  'productManagement.filesOrderNote': 'Files will be shown in the order they appear here. Drag to reorder.',
  'productManagement.moveLeft': 'Move left',
  'productManagement.moveRight': 'Move right',
  'productManagement.image': 'Image',
  'productManagement.video': 'Video',
  'productManagement.noMediaInGallery': 'No media in the gallery',
  'productManagement.clickAddFile': 'Click "Add File" to upload images or videos',
  'productManagement.pricing': 'Price and Discounts',
  'productManagement.price': 'Price (CLP) *',
  'productManagement.discount': 'Discount (%)',
  'productManagement.discountCodes': 'Discount Codes',
  'productManagement.add': 'Add',
  'productManagement.benefits': 'Benefits',
  'productManagement.benefitPlaceholder': 'E.g.: Custom visual design with your brand',
  'productManagement.activeProduct': 'Active product (visible on the public page)',
  'productManagement.saving': 'Saving...',
  'productManagement.saveChanges': 'Save Changes',
  'productManagement.previewTitle': 'Preview - /comprar Page',
  'productManagement.previewLive': 'Changes are reflected in real time',
  'productManagement.noImage': 'No image',
  'productManagement.previewProductName': 'Product Name',
  'productManagement.previewSubtitle': 'Product subtitle',
  'productManagement.previewShortDescription': 'Short product description',
  'productManagement.limitedPromoPrice': 'Limited promotional price (-{pct}%)',
  'productManagement.availableCodes': 'Available codes:',
  'productManagement.trustBadge': 'Over 50 leading companies already trust CONTACT',
  'productManagement.benefitsAndFeatures': 'Benefits and Features',

  // ConfirmDialog.js
  'confirmDialog.title': 'Confirm action',
  'confirmDialog.confirm': 'Confirm',
  'confirmDialog.cancel': 'Cancel',

  // CommandPalette.js
  'commandPalette.placeholder': 'Go to a section...',
  'commandPalette.noResults': 'No results for "{query}"',

  // ForgotPasswordModal.js
  'forgotPassword.sendError': 'Error sending the link',
  'forgotPassword.close': 'Close',
  'forgotPassword.title': 'Recover password',
  'forgotPassword.understood': 'Got it',
  'forgotPassword.body': 'We’ll send you a link to reset your password.',
  'forgotPassword.cancel': 'Cancel',
  'forgotPassword.loading': 'Loading',
  'forgotPassword.sendLink': 'Send link',

  // LockedFeature.js
  'lockedFeature.analytics.title': 'Advanced Analytics',
  'lockedFeature.analytics.description': 'Measure your profile’s performance: visits, clicks by social network, conversions, and real-time trends.',
  'lockedFeature.leads.title': 'Lead Management',
  'lockedFeature.leads.description': 'Capture and organize the contacts interested in your profile. Filter, tag, and export your pipeline.',
  'lockedFeature.teams.title': 'Team Management',
  'lockedFeature.teams.description': 'Share access to your profiles with your team. Assign roles, control permissions, and review activity.',
  'lockedFeature.team_analytics.title': 'Team Analytics',
  'lockedFeature.team_analytics.description': 'Measure the combined performance of all your team’s profiles: visits, clicks, leads, and per-member ranking.',
  'lockedFeature.brand_kit.title': 'Company Brand',
  'lockedFeature.brand_kit.description': 'Define your company’s logo, template, and colors once, and apply them to every profile on your team.',
  'lockedFeature.insights.title': 'AI Insights',
  'lockedFeature.insights.description': 'Get smart recommendations based on your profile visitors’ real behavior.',
  'lockedFeature.exports.title': 'Executive Export',
  'lockedFeature.exports.description': 'Download Excel/PDF reports ready to share with your team or present to clients.',
  'lockedFeature.multiple_profiles.title': 'Multiple Profiles',
  'lockedFeature.multiple_profiles.description': 'Manage several profiles from a single account — ideal for teams, brands, or multiple services.',
  'lockedFeature.hub.title': 'Contact Hub',
  'lockedFeature.hub.description': 'Centralize every contact you capture (manually, leads, card scans) in one place.',
  'lockedFeature.google_sheets.title': 'Integrations',
  'lockedFeature.google_sheets.description': 'Automate sending new contacts and keep your team’s information synced with Google Sheets.',
  'lockedFeature.campaigns.title': 'Events',
  'lockedFeature.campaigns.description': 'Group the leads you capture at a fair or event and measure the return: how many contacts, how many qualified, how many were won.',
  'lockedFeature.reminders.title': 'Automatic Reminders',
  'lockedFeature.reminders.description': 'Automate follow-up on your leads with a scheduled email sequence, without having to remember yourself.',
  'lockedFeature.remove_watermark.title': 'Contact Watermark',
  'lockedFeature.remove_watermark.description': 'Remove the Contact logo from your public profile for a 100% yours look, with no third-party branding.',
  'lockedFeature.developer_api.title': 'Developer API & Webhooks',
  'lockedFeature.developer_api.description': 'Connect Contact with your own systems: create API keys and receive real-time webhooks.',
  'lockedFeature.background_image.title': 'Custom Background Image',
  'lockedFeature.background_image.description': 'Upload your own image as your profile’s background, instead of just using a color.',
  'lockedFeature.products.title': 'Product Catalog',
  'lockedFeature.products.description': 'Show your products or services directly on your digital profile.',
  'lockedFeature.quotations.title': 'Quotes',
  'lockedFeature.quotations.description': 'Receive and manage quote requests directly from your profile.',
  'lockedFeature.genericTitle': '{plan} Feature',
  'lockedFeature.genericDescription': 'Upgrade to {plan} to unlock this feature.',
  'lockedFeature.planLabel': '{plan} Plan',
  'lockedFeature.upgradeTo': 'Upgrade to {plan}',
  'lockedFeature.contactSales': 'Contact sales',
  'lockedFeature.fromPrice': 'From 2,500 CLP / month',

  // NotificationBell.js
  'notificationBell.justNow': 'just now',
  'notificationBell.minAgo': '{n}m ago',
  'notificationBell.hoursAgo': '{n}h ago',
  'notificationBell.daysAgo': '{n}d ago',
  'notificationBell.noRecentActivity': 'No recent activity',
  'notificationBell.newLead': 'New lead:',
  'notificationBell.noName': 'No name',
  'notificationBell.title': 'Notifications',
  'notificationBell.recentActivity': 'Recent activity',
  'notificationBell.close': 'Close',

  // PlansCompareCards.js
  'plansCompare.title': 'Compare plans',
  'plansCompare.subtitle': 'Choose the plan that best fits how you work.',
  'plansCompare.monthly': 'Monthly',
  'plansCompare.yearly': 'Annual',
  'plansCompare.twoMonthsFree': '2 months free',
  'plansCompare.checkoutUnavailable': 'Online payments aren’t available yet. Contact us to activate your plan in the meantime.',
  'plansCompare.currentPlanEndingSoon': 'Your current plan · ending soon',
  'plansCompare.currentPlan': 'Your current plan',
  'plansCompare.recommended': 'Recommended',
  'plansCompare.contactSales': 'Contact sales',
  'plansCompare.yearlySavings': 'You save ${amount} a year',
  'plansCompare.redirecting': 'Redirecting…',
  'plansCompare.renewPlan': 'Renew my plan',
  'plansCompare.free.name': 'Free',
  'plansCompare.free.desc': 'Free forever. Share your contact like a professional, with no time limit.',
  'plansCompare.free.feature.0': 'Unlimited connections',
  'plansCompare.free.feature.1': '1 digital profile',
  'plansCompare.free.feature.2': 'Basic analytics',
  'plansCompare.free.feature.3': 'Limited leads /month',
  'plansCompare.free.feature.4': 'Apple Wallet & Google Wallet',
  'plansCompare.free.feature.5': 'QR code and unique link',
  'plansCompare.free.priceSub': 'forever',
  'plansCompare.free.priceYearlySub': 'forever',
  'plansCompare.free.cta': 'Continue',
  'plansCompare.pro.name': 'Pro',
  'plansCompare.pro.desc': 'For professionals who want to know what’s happening with every contact and act before it goes cold.',
  'plansCompare.pro.feature.0': 'Everything in Free',
  'plansCompare.pro.feature.1': 'Advanced analytics',
  'plansCompare.pro.feature.2': 'Unlimited leads',
  'plansCompare.pro.feature.3': 'Lead identification',
  'plansCompare.pro.feature.4': 'Smart follow-up',
  'plansCompare.pro.feature.5': 'Advanced customization',
  'plansCompare.pro.feature.6': 'Integrations with your tools',
  'plansCompare.pro.feature.7': 'API and Webhooks access',
  'plansCompare.pro.priceSub': '/mo · per profile',
  'plansCompare.pro.priceYearlySub': '/yr · per profile · billed annually',
  'plansCompare.pro.cta': 'Get started with Pro',
  'plansCompare.enterprise.name': 'Enterprise',
  'plansCompare.enterprise.desc': 'For businesses with several people in the field: one central panel to see who’s capturing and converting at each event.',
  'plansCompare.enterprise.feature.0': 'Team ranking by campaign',
  'plansCompare.enterprise.feature.1': 'Multiple managed profiles',
  'plansCompare.enterprise.feature.2': 'Team analytics',
  'plansCompare.enterprise.feature.3': 'Team management',
  'plansCompare.enterprise.feature.4': 'Priority support',
  'plansCompare.enterprise.feature.5': 'Everything in Pro',
  'plansCompare.enterprise.priceTagline': 'Custom pricing based on your team’s size.',
  'plansCompare.enterprise.cta': 'Book a meeting',

  // PlansCompareModal.js
  'plansCompareModal.title': 'Choose how you want to start',
  'plansCompareModal.subtitle': 'You already created your profile. Now choose the plan that best fits you.',
  'plansCompareModal.close': 'Close',

  // ProOnboardingTour.js
  'proTour.continueGuide': 'Continue guide · Step {step} of {total}',
  'proTour.skipGuide': 'Skip guide',
  'proTour.back': 'Back',
  'proTour.viewNow': 'View now',
  'proTour.startUsingPro': 'Start using Pro',
  'proTour.next': 'Next',
  'proTour.step0.title': 'Welcome to Pro',
  'proTour.step0.description': 'Your account now has access to new tools. Before you continue, here’s a quick look at what’s changed.',
  'proTour.step0.bullet0': 'Profiles and customization',
  'proTour.step0.bullet1': 'Leads and follow-up',
  'proTour.step0.bullet2': 'Analytics and campaigns',
  'proTour.step0.bullet3': 'Advanced tools',
  'proTour.step1.title': 'Profiles and customization',
  'proTour.step1.description': 'You can now create up to 3 digital profiles instead of 1, customize themes and backgrounds, and your profile no longer shows the Contact Network watermark.',
  'proTour.step1.bullet0': 'Up to 3 digital profiles',
  'proTour.step1.bullet1': 'Advanced appearance customization',
  'proTour.step1.bullet2': 'No watermark',
  'proTour.step2.title': 'Leads and follow-up',
  'proTour.step2.description': 'Every contact you capture now counts as an unlimited lead, and you can see who scanned your profile. Automatic follow-up alerts you when someone hasn’t replied.',
  'proTour.step2.bullet0': 'Unlimited leads per month',
  'proTour.step2.bullet1': 'See who scanned you',
  'proTour.step2.bullet2': 'Automatic email follow-up',
  'proTour.step3.title': 'Analytics and campaigns',
  'proTour.step3.description': 'Access advanced analytics for your profile and group your leads by event or campaign to know where they came from.',
  'proTour.step3.bullet0': 'Advanced analytics',
  'proTour.step3.bullet1': 'Lead attribution by campaign',
  'proTour.step4.title': 'Advanced tools',
  'proTour.step4.description': 'Connect your favorite tools, use the API and Webhooks to automate processes, and export or quote whenever you need.',
  'proTour.step4.bullet0': 'Integrations (Google Sheets and more)',
  'proTour.step4.bullet1': 'API and Webhooks access',
  'proTour.step4.bullet2': 'Quotes and data export',
  'proTour.step5.title': 'All set',
  'proTour.step5.description': 'You now know the main tools in your Pro plan. You can start using them right now from the dashboard.',

  // ProfileSelectionModal.js
  'profileSelection.loadError': 'We couldn’t load your profiles. Try reloading the page.',
  'profileSelection.chooseAtLeastOne': 'Choose at least one profile to keep active.',
  'profileSelection.saveError': 'We couldn’t save your selection. Try again.',
  'profileSelection.actionRequired': 'Action required',
  'profileSelection.title': 'Choose which profiles to keep active',
  'profileSelection.planAllows': 'Your current plan allows up to {n} {noun}.',
  'profileSelection.activeProfileSingular': 'active profile',
  'profileSelection.activeProfilePlural': 'active profiles',
  'profileSelection.restStayHidden': 'The ones you don’t choose will stay saved, but will stop being public until you activate them again.',
  'profileSelection.loading': 'Loading your profiles…',
  'profileSelection.selectedCount': '{selected} of {total} selected',
  'profileSelection.saving': 'Saving…',
  'profileSelection.confirmSelection': 'Confirm selection',
  'profileSelection.logout': 'Log out',

  // ShortcutsModal.js
  'shortcuts.title': 'Keyboard shortcuts',
  'shortcuts.close': 'Close',
  'shortcuts.search': 'Open quick search',
  'shortcuts.help': 'Show this help',
  'shortcuts.navigate': 'Navigate search results',
  'shortcuts.select': 'Go to the selected section',
  'shortcuts.close_dialogs': 'Close dialogs and modals',

  // ToastStack.js
  'toastStack.undo': 'Undo',
  'toastStack.close': 'Close',

  // AdminCompaniesTab.js
  'adminCompanies.title': 'Registered Companies ({n})',
  'adminCompanies.searchPlaceholder': 'Search by company, industry, or user...',
  'adminCompanies.company': 'Company',
  'adminCompanies.industry': 'Industry',
  'adminCompanies.description': 'Description',
  'adminCompanies.user': 'User',
  'adminCompanies.noDescription': 'No description',
  'adminCompanies.empty': 'No companies registered yet.',

  // AdminRegistrationToggle.js
  'adminRegToggle.saveError': 'We couldn’t save the change. Try again.',
  'adminRegToggle.title': 'User registration',
  'adminRegToggle.loading': 'Loading...',
  'adminRegToggle.active': 'Active',
  'adminRegToggle.disabled': 'Disabled',

  // AdminDisabledSectionsToggle.js
  'adminDisabledSections.section.leads': 'Leads',
  'adminDisabledSections.section.campaigns': 'Events',
  'adminDisabledSections.section.analytics': 'Analytics',
  'adminDisabledSections.section.integrations': 'Integrations',
  'adminDisabledSections.section.hub': 'Hub',
  'adminDisabledSections.section.followups': 'Follow-ups',
  'adminDisabledSections.section.developer': 'Developer',
  'adminDisabledSections.section.teams': 'Teams',
  'adminDisabledSections.section.team_analytics': 'Team Analytics',
  'adminDisabledSections.title': '"Coming Soon" Sections',
  'adminDisabledSections.desc': 'The section stays visible but its buttons show "Coming soon" instead of working.',
  'adminDisabledSections.loading': 'Loading...',

  // AdminPlansTab.js
  'adminPlans.loadError': 'We couldn’t load the current price.',
  'adminPlans.invalidAmounts': 'Enter valid amounts, greater than 0.',
  'adminPlans.saved': 'Price updated. It’s now reflected on the Homepage and the subscription screen.',
  'adminPlans.saveError': 'We couldn’t save the price.',
  'adminPlans.loading': 'Loading prices...',
  'adminPlans.title': 'Pro plan price',
  'adminPlans.subtitle': 'Free is always free and Enterprise is quoted directly with sales — only Pro has an editable price here.',
  'adminPlans.monthlyPrice': 'Monthly price (CLP)',
  'adminPlans.originalValue': 'Original value:',
  'adminPlans.annualPrice': 'Annual price (CLP)',
  'adminPlans.saving': 'Saving…',
  'adminPlans.savePrice': 'Save price',
  'adminPlans.footnote': 'Subscriptions already active on MercadoPago keep billing the amount they were created with; this price applies to new subscriptions from the moment you save it.',

  // AdminCodesTab.js
  'adminCodes.created': 'Code created: {code}',
  'adminCodes.error': 'Error creating the code',
  'adminCodes.createNew': 'Create New Code',
  'adminCodes.generatedSuccessfully': 'Code generated successfully:',
  'adminCodes.numProfilesAllowed': 'Number of allowed profiles',
  'adminCodes.placeholderExample': 'E.g.: 5',
  'adminCodes.creating': 'Creating...',
  'adminCodes.createCode': 'Create code',
  'adminCodes.activationCodes': 'Activation Codes',
  'adminCodes.code': 'Code',
  'adminCodes.status': 'Status',
  'adminCodes.profiles': 'Profiles',
  'adminCodes.createdCol': 'Created',
  'adminCodes.usedCol': 'Used',
  'adminCodes.used': 'Used',
  'adminCodes.available': 'Available',

  // AdminUsersTab.js
  'adminUsers.confirm.deactivate': 'Deactivate {email}? They will lose access to their account immediately.',
  'adminUsers.deactivateUser': 'Deactivate user',
  'adminUsers.deactivate': 'Deactivate',
  'adminUsers.title': 'User Management',
  'adminUsers.user': 'User',
  'adminUsers.status': 'Status',
  'adminUsers.company': 'Company',
  'adminUsers.profiles': 'Profiles',
  'adminUsers.actions': 'Actions',
  'adminUsers.active': 'Active',
  'adminUsers.inactive': 'Inactive',
  'adminUsers.activated': 'Activated',
  'adminUsers.notFilledOut': 'Not filled out',
  'adminUsers.activate': 'Activate',
  'adminUsers.placeholderExample': 'E.g.: 5',
  'adminUsers.numProfiles': '{n} profiles',
  'adminUsers.custom': 'Custom...',

  // AdminWaitlistTab.js (partial — admin panel translation stopped here per user request)
  'adminWaitlist.email': 'Email',
  'adminWaitlist.registeredDate': 'Registration date',

  // App.js (Dashboard shell)
  'dashboard.invalidServerResponse': 'Invalid server response',

  // LockedFeatureMocks.js
  'lockedFeatureMock.active': 'Active',
  'lockedFeatureMock.inactive': 'Inactive',
  'lockedFeatureMock.analytics.totalVisits': 'Total Visits',
  'lockedFeatureMock.analytics.socialClicks': 'Social Clicks',
  'lockedFeatureMock.analytics.savedContacts': 'Saved Contacts',
  'lockedFeatureMock.analytics.engagementRate': 'Engagement Rate',
  'lockedFeatureMock.analytics.chartTitle': 'Visits · last 14 days',
  'lockedFeatureMock.teams.sales': 'Sales',
  'lockedFeatureMock.teams.marketing': 'Marketing',
  'lockedFeatureMock.teams.support': 'Support',
  'lockedFeatureMock.teams.members': 'Members',
  'lockedFeatureMock.teams.activeStat': 'Active',
  'lockedFeatureMock.teams.profiles': 'Profiles',
  'lockedFeatureMock.teamAnalytics.visits': 'Visits',
  'lockedFeatureMock.teamAnalytics.clicks': 'Clicks',
  'lockedFeatureMock.hub.contacts': 'Contacts',
  'lockedFeatureMock.hub.newStat': 'New',
  'lockedFeatureMock.hub.meetings': 'Meetings',
  'lockedFeatureMock.hub.wonStat': 'Won',
  'lockedFeatureMock.campaigns.activeEvents': 'Active events',
  'lockedFeatureMock.campaigns.conversion': 'Conversion',
  'lockedFeatureMock.reminders.day3': 'Day 3',
  'lockedFeatureMock.reminders.day6': 'Day 6',
  'lockedFeatureMock.brandKit.companyLogo': 'Company logo',
  'lockedFeatureMock.brandKit.appliesTo': 'Applies to your team’s profiles',
  'lockedFeatureMock.brandKit.applyToProfiles': 'Apply to 4 of 4 profiles',
  'lockedFeatureMock.brandKit.apply': 'Apply',
};
